(ns io.repliance.questionnaire.utils
  (:require [clojure.set :refer [map-invert]]
            [clojure.string :as str]))

(defn set-value-exceptions
  "Override all content values that have been
   explicitly declared in `{:value-exceptions {ri {k exception value}}}`
  "
  [value-exceptions {:keys [row-index] :as row-result}]
  (into row-result
        (map (fn [[kw-to-overwrite exception]]
               {kw-to-overwrite {:content  (:value exception)
                                 :original (get-in row-result [kw-to-overwrite :content])}}))
        (get value-exceptions row-index)))

(defn set-ref-exceptions
  "
  If there's reference exceptions set for the given row index.
  Replace the value set by the sheets global column configuration
  "
  [ref-exceptions {:keys [row-index] :as row-result}]
  (into row-result
        (map (fn [[kw-to-overwrite exception]]
               {kw-to-overwrite (assoc exception :is-exception? true)})
             (get ref-exceptions row-index))))

(defn question-num-to-depth
  "
  Calculate the depth from a question number.
  The expected format is <string>.1.1 etc.
  Take the numeric components and calculate length.
  This is originally from the SIG question number format.
  "
  [question-num]
  (when question-num
    ;; Make sure that question-num is a string.
    (-> (str question-num)
        (str/replace #"[a-zA-Z]*" "")
        (str/replace #"^." "")
        (str/replace #".0$" "") ;; remove trailing 0 e.g. "1.0" -> "1"
        (str/split #"\.")
        (count))))


(defn row-to-result
  "
   collect the cells pointed by the columns
   e.g. (def columns {1 :num 3 :response})
   reduce row data to:
      {:row-index i
       :num       {:col 1 :content <content-of col 1>}
       :response  {:col 3 :content <content-of col 3>}}
  "
  [sheet columns depth-source  [row-index row-col-data]]
  (let [key-to-row         (map-invert columns)
        configured-columns (set (vals columns))
        question-num       (when (contains? configured-columns :num)
                             (:content (nth row-col-data (:num key-to-row))))

        initial-depth (when (= depth-source :from-number)
                        (question-num-to-depth question-num))]
    (reduce (fn [acc [col-index col-keyword]]
              (merge acc
                     {col-keyword
                      ;; This referes to the columns that are
                      ;; selected explicitly with dropdowns.
                      (if (contains? (if (vector? row-col-data)
                                       row-col-data
                                       (vec row-col-data))
                                     col-index)
                        (nth row-col-data col-index)
                        ;; In case the column wasn't present in the source array,
                        ;; we still create a result with nil :content
                        {:content nil
                         :row row-index
                         :col col-index})}))
            {:row-index row-index
             :sheet     sheet
             :depth     {:content (if initial-depth
                                    initial-depth
                                    1 ; Default to depth = 1
                                    )}}
            columns)))

(defn is-question-row?
  "
   The data comes in the form {:rows [[row-index [cell-vec cell-vec]] ... ]}
   "
  [question-rows [row-index _]]
  (contains? question-rows row-index))

(defn coerce-to-int [value]
  #?(:clj
     (cond
       (double? value) (.intValue value)
       (int? value)    value
       (string? value) (if-not (str/blank? value)
                         (Integer/parseInt value)
                         1))
     :cljs (cond
             (int? value)    value
             (string? value) (if-not (str/blank? value)
                               (js/parseInt value)
                               1))))

;; Build question paths for results

(defn build-question-path
  "

  Build path for question result based on the {:depth {:content int?}}
  value and the information that the accumulater carries with.

  Use the path of previous item with (dec current-depth) as basis.

  Parameters:

  sheet-uuid         :use as a base path
  accumulator
  - depths   [:map-of Depth RowIndex] where Depth int? RowIndex int?
  - paths    [:map-of Depth [:cat [:or string? uuid?] [:* int?]]]
  - items    []

  As an initial accumulator  {:paths {0 [sheet-uuid]}} is sufficient.
  But if {:items []} not provided the resulting items are returned
  in reversed order in a list instead of a vector.

  result [:map [:row-index int?
                :depth [:map [:content
                              [:or int?
                                   double?
                                   string?]]]]]
  "
  [{:keys [depths paths items]} result]

  (let [;; Current-depth will be used as a key in a map
        ;; make sure that the value will be treated as
        ;; integer.
        current-depth (coerce-to-int (get-in result [:depth :content] 1))

        current-path     (conj (get paths (dec current-depth))
                               (:row-index result))

        ;; Preceding refers to the preceding row index that has
        ;; the same depth as the current result. This is needed
        ;; to be able to see if the question depth can be increased.
        ;;
        ;; If a question do not have a preceding row index associated
        ;; (inc depth) would result in a situation where there would
        ;; not be a parent available for building the path.
        result-with-path (merge result
                                {:preceding (get depths current-depth)
                                 :path      current-path})]

    ;; Keep track of the depths and paths
    {:depths (assoc depths current-depth (:row-index result))
     :paths  (assoc paths current-depth current-path)
     :items  (conj items  result-with-path)}))

(defn apply-parsing-configuration-to-rows
  "
  Given sheet in edn format and a sheet parsing configuration.
  Return sheet question extraction results in format that is
  used by the workbook-workspace results listing.
  "
  [{{:keys [rows sheet uuid]}        :sheet
    {:keys [columns
            depth-source
            question-rows
            ref-exceptions
            value-exceptions]
     :or   {depth-source :explicit}} :parsing-config}]

  (let [xf-filter-question-rows   (filter (partial is-question-row? question-rows))
        xf-rows-to-results        (map (partial row-to-result sheet columns depth-source))
        xf-apply-ref-exceptions   (map (partial set-ref-exceptions ref-exceptions))
        xf-apply-value-exceptions (map (partial set-value-exceptions value-exceptions))]

    (:items (transduce (comp xf-filter-question-rows
                             xf-rows-to-results
                             xf-apply-ref-exceptions
                             xf-apply-value-exceptions)

                       (completing build-question-path)

                       {:paths  {0 [uuid]}
                        :items  []}

                       rows))))

(defn index-attachment
  "Adds attachment to an answer->attachment index.

  The index is formatted as answer-id -> [attachment-id]"
  [index attachment]
  (reduce (fn [index answer-id]
            (update index answer-id (fnil conj #{}) attachment))
          index
          (:linked-answers attachment [])))

(defn attachments-to-answers-index
  "Returns an index of answers to attachments"
  [attachments]
  (->> (reduce index-attachment {} attachments)
       (map #(vector (key %) (set (val %))))
       (into {})))
